import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Play, Clock, TrendingUp, CheckCircle2, AlertCircle } from 'lucide-react';

export const ExerciseLibrary = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');

  const exercises = [
    {
      id: 1,
      name: 'Pendulum Exercise',
      category: 'range-of-motion',
      level: 'beginner',
      duration: '5 minutes',
      reps: '10-15 circles each direction',
      phase: 'Early Recovery',
      description: 'Gentle swinging motion to improve shoulder mobility without muscle activation.',
      videoUrl: 'https://www.youtube.com/embed/O7bn6aHdeZY',
      benefits: ['Reduces stiffness', 'Improves circulation', 'Gentle on healing tissues'],
      precautions: ['Avoid if severe pain', 'Use pain-free range only']
    },
    {
      id: 2,
      name: 'Wall Slides',
      category: 'range-of-motion',
      level: 'beginner',
      duration: '10 minutes',
      reps: '10-12 repetitions',
      phase: 'Early-Mid Recovery',
      description: 'Gradual overhead reaching exercise using wall for support.',
      videoUrl: 'https://www.youtube.com/embed/TuhBnvDEPJg',
      benefits: ['Improves overhead reach', 'Stretches shoulder capsule', 'Safe progression'],
      precautions: ['Stop if sharp pain', 'Maintain good posture']
    },
    {
      id: 3,
      name: 'External Rotation with Band',
      category: 'strengthening',
      level: 'intermediate',
      duration: '10 minutes',
      reps: '3 sets of 12-15',
      phase: 'Mid-Late Recovery',
      description: 'Strengthens rotator cuff muscles responsible for shoulder stability.',
      videoUrl: 'https://www.youtube.com/embed/7dQwWMPF1KY',
      benefits: ['Strengthens rotator cuff', 'Improves stability', 'Prevents re-injury'],
      precautions: ['Use light resistance initially', 'Keep elbow at side']
    },
    {
      id: 4,
      name: 'Scapular Squeezes',
      category: 'strengthening',
      level: 'beginner',
      duration: '5 minutes',
      reps: '15-20 holds',
      phase: 'All Phases',
      description: 'Strengthens muscles that stabilize the shoulder blade.',
      videoUrl: 'https://www.youtube.com/embed/bQVQMh23kxw',
      benefits: ['Improves posture', 'Reduces shoulder impingement', 'Foundation exercise'],
      precautions: ['Avoid shrugging shoulders', 'Gentle controlled motion']
    },
    {
      id: 5,
      name: 'Doorway Stretch',
      category: 'stretching',
      level: 'beginner',
      duration: '5 minutes',
      reps: 'Hold 30 seconds, 3-4 times',
      phase: 'All Phases',
      description: 'Stretches chest and front shoulder muscles.',
      videoUrl: 'https://www.youtube.com/embed/B1kN2oDQCN0',
      benefits: ['Improves flexibility', 'Reduces tightness', 'Better posture'],
      precautions: ['Gentle stretch only', 'No bouncing']
    },
    {
      id: 6,
      name: 'Internal Rotation Stretch',
      category: 'stretching',
      level: 'intermediate',
      duration: '5 minutes',
      reps: 'Hold 30 seconds, 3 times',
      phase: 'Mid-Late Recovery',
      description: 'Improves internal rotation range of motion.',
      videoUrl: 'https://www.youtube.com/embed/JONJbfKbN8Y',
      benefits: ['Increases rotation', 'Functional movement', 'Daily activity improvement'],
      precautions: ['Pain-free range', 'Gradual progression']
    },
    {
      id: 7,
      name: 'Shoulder Shrugs',
      category: 'strengthening',
      level: 'beginner',
      duration: '5 minutes',
      reps: '3 sets of 15',
      phase: 'Mid Recovery',
      description: 'Strengthens upper trapezius and improves shoulder elevation.',
      videoUrl: 'https://www.youtube.com/embed/cMHNv1l3BYo',
      benefits: ['Strengthens upper back', 'Improves shoulder mechanics', 'Easy to perform'],
      precautions: ['Avoid excessive weight', 'Controlled motion']
    },
    {
      id: 8,
      name: 'Lat Pulldown',
      category: 'strengthening',
      level: 'advanced',
      duration: '15 minutes',
      reps: '3 sets of 10-12',
      phase: 'Late Recovery',
      description: 'Advanced strengthening for shoulder and back muscles.',
      videoUrl: 'https://www.youtube.com/embed/JgeOYeZoWKo',
      benefits: ['Full shoulder strength', 'Functional power', 'Return to activities'],
      precautions: ['Only after doctor clearance', 'Progress gradually']
    }
  ];

  const categories = [
    { value: 'all', label: 'All Exercises' },
    { value: 'range-of-motion', label: 'Range of Motion' },
    { value: 'strengthening', label: 'Strengthening' },
    { value: 'stretching', label: 'Stretching' }
  ];

  const levels = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const filteredExercises = exercises.filter(ex => {
    const matchesCategory = selectedCategory === 'all' || ex.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || ex.level === selectedLevel;
    return matchesCategory && matchesLevel;
  });

  const getLevelColor = (level) => {
    switch(level) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Exercise Library
          </h2>
          <p className="text-lg text-gray-600 mb-2">
            Professional exercises for shoulder recovery and strengthening
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <AlertCircle className="w-4 h-4" />
            <span>Always consult your physiotherapist before starting any exercise program</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <Button
                key={cat.value}
                variant={selectedCategory === cat.value ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat.value)}
                className={selectedCategory === cat.value ? 'bg-medical-orange hover:bg-orange-600' : ''}
              >
                {cat.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {levels.map(lvl => (
              <Button
                key={lvl.value}
                variant={selectedLevel === lvl.value ? 'default' : 'outline'}
                onClick={() => setSelectedLevel(lvl.value)}
                size="sm"
                className={selectedLevel === lvl.value ? 'bg-gray-800 hover:bg-gray-900' : ''}
              >
                {lvl.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Exercise Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {filteredExercises.map(exercise => (
            <Card key={exercise.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              {/* Video Embed */}
              <div className="aspect-video bg-gray-900 relative">
                <iframe
                  src={exercise.videoUrl}
                  title={exercise.name}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <CardTitle className="text-xl">{exercise.name}</CardTitle>
                  <Badge className={getLevelColor(exercise.level)}>
                    {exercise.level}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {exercise.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    {exercise.phase}
                  </span>
                </div>
                <CardDescription className="mt-2">{exercise.description}</CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">Repetitions:</p>
                    <p className="text-sm text-gray-600">{exercise.reps}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Benefits:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {exercise.benefits.map((benefit, idx) => (
                        <li key={idx}>• {benefit}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4 text-orange-600" />
                      Precautions:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {exercise.precautions.map((precaution, idx) => (
                        <li key={idx}>• {precaution}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredExercises.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No exercises match your selected filters.</p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center bg-orange-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Need a Personalized Exercise Program?
          </h3>
          <p className="text-gray-700 mb-6">
            Get a customized rehabilitation program tailored to your specific shoulder condition.
          </p>
          <a href="https://drdnreddyortho.com/#shoulder" target="_blank" rel="noopener noreferrer">
            <Button className="bg-medical-orange hover:bg-orange-600 text-white" size="lg">
              Consult Dr Reddy
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};